import { useState, type FormEvent } from 'react'
import { Send, CheckCircle2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useSeo } from '@/lib/useSeo'
import { PageHeader } from '@/components/public/PageHeader'

export function Contact() {
  useSeo({
    title: 'Contact us',
    description: 'Contact the VICE team. For support, access requests, or questions, send us a message.',
  })

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const { error: err } = await supabase.from('contact_submissions').insert({
      name,
      email,
      subject,
      message,
      source: 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    })
    setSubmitting(false)
    if (err) {
      setError(err.message)
      return
    }
    setSent(true)
  }

  return (
    <>
      <PageHeader
        eyebrow="Contact"
        title="Get in touch."
        lede="For access requests, support questions, vulnerability reports, or feedback — we'd like to hear from you."
      />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        {sent ? (
          <div className="bg-white rounded-lg border-2 border-success p-8 text-center" role="status" aria-live="polite">
            <div className="mx-auto inline-flex p-3 rounded-full bg-success/10 text-success">
              <CheckCircle2 size={32} aria-hidden="true" />
            </div>
            <h2 className="mt-4 text-2xl font-bold text-primary">Message received</h2>
            <p className="mt-2 text-neutral-700">
              Thanks, {name || 'friend'}. We&apos;ll reply to <strong>{email}</strong> shortly.
            </p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="bg-white border border-neutral-200 rounded-lg shadow-sm p-8 space-y-5"
            aria-labelledby="contact-title"
          >
            <h2 id="contact-title" className="sr-only">
              Contact form
            </h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wide text-neutral-700 mb-1">
                  Name
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  maxLength={120}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full h-10 px-3 rounded border border-neutral-300 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wide text-neutral-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  maxLength={250}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-10 px-3 rounded border border-neutral-300 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
                />
              </div>
            </div>
            <div>
              <label htmlFor="subject" className="block text-xs font-semibold uppercase tracking-wide text-neutral-700 mb-1">
                Subject
              </label>
              <input
                id="subject"
                type="text"
                required
                maxLength={200}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full h-10 px-3 rounded border border-neutral-300 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
              />
            </div>
            <div>
              <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wide text-neutral-700 mb-1">
                Message
              </label>
              <textarea
                id="message"
                required
                rows={7}
                maxLength={5000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-3 py-2 rounded border border-neutral-300 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
              />
              <p className="mt-1 text-xs text-neutral-500">{message.length} / 5000</p>
            </div>

            {error && (
              <p role="alert" className="text-sm text-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded bg-primary text-white font-semibold hover:bg-primary-dark disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
            >
              <Send size={16} aria-hidden="true" />
              {submitting ? 'Sending…' : 'Send message'}
            </button>
            <p className="text-xs text-neutral-500">
              We respect your inbox. We&apos;ll only use your email to reply to this message.
            </p>
          </form>
        )}
      </section>
    </>
  )
}
